import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import type { LiveUpdatesStatusKey } from 'api/liveUpdates.types'
import Information from 'components/Icons/Information'
import palette from 'constants/palette'
import { buildTranslate } from 'locales'
import React from 'react'
import LockedCard from './LockedCard'
import type { LiveUpdatesLockedMode, LiveUpdatesWebhookGate } from './sections'
import type { OloWebhookPendingState } from './trigger'

const t = buildTranslate('live_updates')
const tGate = buildTranslate('live_updates.webhook_gate')

const SECTION_GAP = '16px'
const NOTE_GAP = '8px'

// FR-75 lock-card body copy per delivery mode. The title is shared
// (`locked.delivery_unavailable_title`); only the explanatory body differs.
const LOCKED_BODY_KEY: Record<LiveUpdatesLockedMode, string> = {
  self_managed: 'locked.delivery_self_managed_body',
  disabled: 'locked.delivery_disabled_body',
}

type GateCopy = {
  title: string
  body: string
  actionLabel: string
  actionAria: string
}

// Gate-card copy keys, spelled out rather than interpolated from the section/
// state values: a rename of either union must break the build, not silently
// render a raw key.
const GATE_KEY: Record<
  LiveUpdatesWebhookGate,
  Record<OloWebhookPendingState, GateCopy>
> = {
  delivery: {
    not_connected: {
      title: 'delivery_title',
      body: 'delivery_body',
      actionLabel: 'set_up',
      actionAria: 'set_up_delivery_aria',
    },
    waiting: {
      title: 'delivery_waiting_title',
      body: 'delivery_waiting_body',
      actionLabel: 'review_setup',
      actionAria: 'review_setup_delivery_aria',
    },
  },
  canceled: {
    not_connected: {
      title: 'canceled_title',
      body: 'canceled_body',
      actionLabel: 'set_up',
      actionAria: 'set_up_canceled_aria',
    },
    waiting: {
      title: 'canceled_waiting_title',
      body: 'canceled_waiting_body',
      actionLabel: 'review_setup',
      actionAria: 'review_setup_canceled_aria',
    },
  },
}

// Only 'not_connected' and 'waiting' ever reach the gate card — a 'connected'
// section renders its real status cards instead. That is enforced in the TYPE
// rather than by a runtime guard: this scene has no error boundary, so a throw
// here would take the whole Live Updates page down, and the caller that widens
// this path should fail to compile instead.
const gateCopyFor = (
  section: LiveUpdatesWebhookGate,
  connectionState: OloWebhookPendingState
): GateCopy => GATE_KEY[section][connectionState]

// The connect-webhooks card's only control is its set-up/review link, so a
// gate without a handler would render a live-looking link that does nothing.
// Bundled, omitting the handler is a compile error.
export interface WebhookGatePanel {
  section: LiveUpdatesWebhookGate
  /** Only 'not_connected' | 'waiting' ever reach here — 'connected' sections
   * render their real status cards instead of this gate, so the type excludes
   * it outright rather than trusting the caller to narrow. */
  connectionState: OloWebhookPendingState
  onSetUp: () => void
}

export interface StatusSectionProps {
  /** Header translation key relative to the `live_updates` root
   * (e.g. `sections.delivery`). */
  titleKey: string
  /** Optional info-note key relative to the `live_updates` root
   * (e.g. `shared_note`, `sections.ready_note`). */
  noteKey?: string
  /** FR-75: when set, a lock card replaces the plain header + status cards and
   * explains why the delivery updates can't be sent. */
  lockedMode?: LiveUpdatesLockedMode
  /** When set, a connect-Olo-webhooks card replaces the status cards — these
   * statuses only arrive as Olo webhooks, and none are configured yet. */
  webhookGate?: WebhookGatePanel
  /** Ordered statuses the lock / webhook-gate card lists as suppressed. Only
   * meaningful alongside `lockedMode` or `webhookGate`. */
  suppressedStatuses?: LiveUpdatesStatusKey[]
  /** The section's status cards. Not rendered when the section is replaced. */
  children: React.ReactNode
}

// A page section wrapper for the Live Updates config page: header, an optional
// grey info note, and the section's status cards. When `lockedMode` (FR-75) or
// `webhookGate` is set the note + cards are replaced by a single card listing
// the messages that can't be sent. Presentational only — grouping, lock, and
// gate decisions live in `sectionForStatuses`.
const StatusSection = ({
  titleKey,
  noteKey,
  lockedMode,
  webhookGate,
  suppressedStatuses = [],
  children,
}: StatusSectionProps) => {
  const [css] = useStyletron()

  const suppressedRows = suppressedStatuses.map(status => ({
    key: status,
    label: t(`locked.suppressed.${status}`),
  }))

  const renderBody = () => {
    if (lockedMode) {
      return (
        <LockedCard
          title={t('locked.delivery_unavailable_title')}
          headingTag="h3"
          body={t(LOCKED_BODY_KEY[lockedMode])}
          rows={suppressedRows}
        />
      )
    }

    if (webhookGate) {
      const copy = gateCopyFor(webhookGate.section, webhookGate.connectionState)
      return (
        <LockedCard
          headerIcon="webhook"
          title={tGate(copy.title)}
          headingTag="h3"
          body={tGate(copy.body)}
          action={{
            label: tGate(copy.actionLabel),
            ariaLabel: tGate(copy.actionAria),
            onClick: webhookGate.onSetUp,
          }}
          rows={suppressedRows}
        />
      )
    }

    return (
      <>
        {noteKey && (
          <div
            className={css({
              display: 'flex',
              alignItems: 'center',
              gap: NOTE_GAP,
            })}
          >
            <span
              aria-hidden="true"
              className={css({ display: 'flex', flexShrink: 0 })}
            >
              <Information type="info" fillColor={palette.grey70} />
            </span>
            <Text.BodyText4 color="grey70">{t(noteKey)}</Text.BodyText4>
          </div>
        )}

        {children}
      </>
    )
  }

  return (
    <section
      className={css({
        display: 'flex',
        flexDirection: 'column',
        gap: SECTION_GAP,
      })}
    >
      <Text.Header3 tag="h2" className="m-0">
        {t(titleKey)}
      </Text.Header3>

      {renderBody()}
    </section>
  )
}

export default StatusSection
